import { useState, useEffect } from 'react';
import { Panel, Input, Button, CellList, CellSimple, Typography, Spinner } from '@maxhub/max-ui';
import { useHousesStore } from '../store/houses.store.ts';
import { createReading } from '../api/houses.ts';
import { getCurrentPeriod, todayIso, formatPeriod } from '../utils/period.ts';

const METER_LABEL: Record<string, string> = {
  cold: '🔵 Холодная вода',
  hot: '🔴 Горячая вода',
};

interface SubmitReadingPageProps {
  meterId: string;
  onBack: () => void;
  onSuccess: () => void;
}

export function SubmitReadingPage({ meterId, onBack, onSuccess }: SubmitReadingPageProps) {
  const { meters, selectedHouseId, readings, loadReadings, addReadingToStore } = useHousesStore();

  const houseMeters = selectedHouseId ? (meters[selectedHouseId] ?? []) : [];
  const meter = houseMeters.find((m) => m.id === meterId) ?? null;
  const meterReadings = readings[meterId] ?? [];
  const lastReading = meterReadings[0] ?? meter?.readings?.[0] ?? null;

  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const period = getCurrentPeriod();
  const today = todayIso();

  useEffect(() => {
    if (meterId) loadReadings(meterId);
  }, [meterId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    const numValue = parseFloat(value.replace(',', '.'));
    if (isNaN(numValue) || numValue < 0) {
      setError('Введите корректное значение');
      return;
    }

    if (lastReading && numValue < Number(lastReading.value)) {
      setError(
        `Значение не может быть меньше предыдущего (${Number(lastReading.value).toFixed(2)} м³)`
      );
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const reading = await createReading(meterId, {
        value: numValue,
        readingDate: today,
        period,
      });
      addReadingToStore(meterId, reading);
      onSuccess();
    } catch (err: unknown) {
      const msg = await extractErrorMessage(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!meter) {
    return (
      <Panel style={{ padding: '1rem', maxWidth: 480, margin: '0 auto' }}>
        <Button mode="link" onClick={onBack}>← Назад</Button>
        <Typography.Label style={{ display: 'block', color: '#c62828', marginTop: '1rem' }}>
          Счётчик не найден
        </Typography.Label>
      </Panel>
    );
  }

  return (
    <Panel style={{ padding: '1rem', maxWidth: 480, margin: '0 auto' }}>
      <Button mode="link" onClick={onBack} style={{ marginBottom: '1rem' }}>
        ← Назад
      </Button>

      <Typography.Headline variant="medium-strong" style={{ display: 'block', marginBottom: '0.25rem' }}>
        {METER_LABEL[meter.type] ?? meter.type}
      </Typography.Headline>

      {meter.serialNumber && (
        <Typography.Label style={{ display: 'block', opacity: 0.6, marginBottom: '0.75rem' }}>
          Серийный номер: {meter.serialNumber}
        </Typography.Label>
      )}

      <div style={{ marginBottom: '1.25rem' }}>
        <Typography.Label
          style={{
            display: 'inline-block',
            background: 'var(--color-bg-secondary, #e8f5e9)',
            borderRadius: 20,
            padding: '0.25rem 0.75rem',
            fontWeight: 600,
          }}
        >
          {formatPeriod(period)}
        </Typography.Label>
      </div>

      {lastReading && (
        <CellList mode="island" style={{ marginBottom: '1.25rem' }}>
          <CellSimple
            title="Предыдущее показание"
            subtitle={formatPeriod(lastReading.period)}
            after={
              <Typography.Headline variant="medium-strong">
                {Number(lastReading.value).toFixed(2)} м³
              </Typography.Headline>
            }
          />
        </CellList>
      )}

      <div style={{ marginBottom: '1rem' }}>
        <Typography.Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
          Текущее показание (м³)
        </Typography.Label>
        <Input
          mode="primary"
          type="number"
          inputMode="decimal"
          step="0.01"
          min={lastReading ? Number(lastReading.value) : 0}
          placeholder={lastReading ? `≥ ${Number(lastReading.value).toFixed(2)}` : '0.00'}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          disabled={submitting}
          autoFocus
          style={{ textAlign: 'right', fontSize: '1.25rem', fontWeight: 600 }}
        />
      </div>

      {error && (
        <Typography.Label style={{ display: 'block', color: '#c62828', marginBottom: '1rem' }}>
          {error}
        </Typography.Label>
      )}

      <Button
        mode="primary"
        size="large"
        stretched
        loading={submitting}
        disabled={submitting || !value}
        onClick={handleSubmit}
      >
        {submitting ? <Spinner size={20} /> : 'Подать показание'}
      </Button>
    </Panel>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function extractErrorMessage(err: unknown): Promise<string> {
  try {
    if (err && typeof err === 'object' && 'response' in err) {
      const res = (err as { response: Response }).response;
      const body = await res.json();
      return body?.error ?? 'Ошибка при отправке';
    }
  } catch {
    // ignore
  }
  return 'Ошибка при отправке';
}
